import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '@components/Layout';
import Entity from '@components/Entity/Entity';
import type { EntityTypeUnion } from '@einhaus/project314-types';

interface EntityPageProps {
    entityType: EntityTypeUnion['entityType'];
}

const EntityPage = ({ entityType }: EntityPageProps) => {
    const { id = '' } = useParams<{ id: string }>();
    const entityKey = `${entityType}:${id}`;

    // Track the loaded name alongside the entity it belongs to so a stale name
    // is never shown after the ID or entity type changes.
    const [loadedEntity, setLoadedEntity] = useState<{ id: string; entityType: EntityTypeUnion['entityType']; name: string } | null>(null);

    // Create a callback to receive entity data from the Entity component
    const handleEntityDataLoaded = useCallback(
        (data: { name: string }) => {
            if (data?.name) {
                setLoadedEntity((currentLoadedEntity) => {
                    if (
                        currentLoadedEntity?.id === id &&
                        currentLoadedEntity.entityType === entityType &&
                        currentLoadedEntity.name === data.name
                    ) {
                        return currentLoadedEntity;
                    }

                    return { id, entityType, name: data.name };
                });
            }
        },
        [entityType, id]
    );

    const entityName = loadedEntity?.id === id && loadedEntity.entityType === entityType ? loadedEntity.name : '';

    // Determine the page title - use entity name if available, otherwise use ID
    const pageTitle = entityName ? `${entityName}` : `${id}`;

    return (
        <Layout containerSize="1800px" title={`${pageTitle} - Project 314`}>
            <div className="mt-3">
                <Entity entityId={+id} entityType={entityType} key={entityKey} onDataLoaded={handleEntityDataLoaded} />
            </div>
        </Layout>
    );
};

export default EntityPage;
